import type { FlagGetter } from "@/FeatureFlag";
import type { Feature } from "@/types/feature";

// One row per feature. Adding a new launch follows this shape: pick the
// unlock flag, the route prefix, and toggle which state-machine branches
// apply. The access hook walks these flags in order — when none of the
// optional branches are enabled the machine collapses to coming-soon → feature.
export type FeatureConfig = {
  // Root path used by the child-route guard when redirecting non-feature
  // states back to the splash root. Must match the route registered in App.tsx.
  rootPath: string;
  // Unlock predicate. Receives the flag getter so any combination of flags can
  // be checked. Omit when the feature is GA for everyone.
  unlockFn?: (flag: FlagGetter) => boolean;
  // Gate the per-user RBAC check (NoPermissions splash). Today the CRO RBAC
  // stub maps to userIsCROAdmin — features outside that scope skip it.
  requiresRBAC: boolean;
  // ai_chat-only consent gate (NeedsAiConsent splash). Other features skip the
  // approval query.
  requiresAiConsent: boolean;
  // Engagement (get-started splash) is opt-in. Features without an associated
  // Get Started CTA leave this false so the access hook resolves directly
  // from `coming-soon` to `feature` once the unlock flag flips.
  requiresEngagement: boolean;
};

// Non-AI CRO features only need the feature flag, not the AI contract gate.
const croUnlocked = (flag: FlagGetter) => flag("freshpaintAiChat");

// ai_chat requires both flags — the AI contract gate applies to the AI product itself.
const freshpaintAiChatEnabled = (flag: FlagGetter) =>
  flag("freshpaintAiChat") && flag("contractAllowsAIUsage");

// Single source of truth for "is this feature unlocked for the account?" —
// evaluates the feature's unlock predicate against the current flag getter.
// Features with no unlockFn are GA for everyone. Prefer this over inlining the
// raw unlock flag at call sites so the flag→feature mapping stays in one place.
export function isFeatureUnlocked(feature: Feature, flag: FlagGetter): boolean {
  const config = FEATURES[feature];
  return config.unlockFn != null ? config.unlockFn(flag) : true;
}

export const FEATURES: Record<Feature, FeatureConfig> = {
  ai_chat: {
    rootPath: "/ai-chat",
    unlockFn: freshpaintAiChatEnabled,
    requiresRBAC: true,
    requiresAiConsent: true,
    requiresEngagement: true,
  },
  web_analytics: {
    rootPath: "/web-analytics",
    unlockFn: croUnlocked,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  funnels: {
    rootPath: "/funnels",
    unlockFn: croUnlocked,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  heatmaps: {
    rootPath: "/heatmaps",
    unlockFn: freshpaintAiChatEnabled,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  session_replay: {
    rootPath: "/session-replay",
    unlockFn: freshpaintAiChatEnabled,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  surveys: {
    rootPath: "/surveys",
    unlockFn: croUnlocked,
    requiresRBAC: true,
    requiresAiConsent: false,
    requiresEngagement: true,
  },
  segments: {
    rootPath: "/audiences/segments",
    unlockFn: (flag) => flag("audiences") || flag("tempSegmentsM1"),
    requiresRBAC: false,
    // Existing audiences customers must reach the feature without a one-time
    // Get Started splash (CE-1550 AC #5). Flip to true once we have a backfill
    // for engagement on accounts that already had the audiences flag.
    requiresEngagement: false,
    requiresAiConsent: false,
  },
  personas: {
    rootPath: "/personas",
    // Personas ride on Freshpaint AI access, mapped to the freshpaintai RBAC
    // domain (see useHasRBACAccessToFeature). Roles without it (data_viewer,
    // event_manager) get the no-permissions splash instead of a surface the
    // backend would reject — the persona endpoints all require RequireAIAccessV1
    // (DomainFreshpaintAI:read), which only admin/data_manager/general_user hold.
    // The freshpaintai domain has no "write" level, so permitted roles resolve to
    // readOnly:true here; the persona surface intentionally ignores that flag
    // (editing is governed by AI access, which every permitted role has).
    requiresRBAC: true,
    // The persona data endpoints require AI usage consent on the backend
    // (RequireAIAccessV1), so gate on consent up front — otherwise the list
    // fetch 403s into a generic error for accounts that haven't enabled AI.
    requiresAiConsent: true,
    // First-time users see the Get Started splash before the persona surface.
    requiresEngagement: true,
  },
  data_imports: {
    rootPath: "/imports",
    unlockFn: (flag) => flag("tempNewUploader"),
    // Imports is a Sources sub-page, not a CRO splash feature. The registry
    // entry exists so product-update signups (setup-call requests) can use the
    // shared Feature type; page access is gated by tempNewUploader in App.tsx.
    requiresRBAC: false,
    requiresAiConsent: false,
    requiresEngagement: false,
  },
  attribution: {
    rootPath: "/attribution",
    // Attribution is an existing entitlement-gated page, not a CRO splash
    // feature. This entry exists so product-update signups (the Insights
    // "Talk to us about access" CTA) can use the shared Feature type; page
    // access is gated by entitlements, not this registry.
    requiresRBAC: false,
    requiresAiConsent: false,
    requiresEngagement: false,
  },
};
